// Types
import { Nullable } from "@Common/interfaces/types";
import { AuthenticationType } from "@Settings/types/Common/auth";
import { AuthRoleStatus, TProviderRole, TSelectedProvider } from "@Settings/types/administrationTools/utils";
import { IGlobalPositioningService, IKeyManagementSystem } from "@Settings/types/administrationTools/model";
import { TGetProviderList } from "@Settings/api/AdministrationTools/queries";

type TGetSelectedProvider = {
	providers: Nullable<TGetProviderList[]>;
	selectedProvider: Partial<TSelectedProvider> | null;
	adminToolsProvider: IGlobalPositioningService | IKeyManagementSystem;
};

type UpdateProvider = {
	providerName: string;
	providerDBProperty: string;
	authRoleStatuses?: AuthRoleStatus[];
	authType?: AuthenticationType;
	auth: {
		roles: {
			role: string;
			authType: AuthenticationType;
			properties: string;
		}[];
	};
};

const isDBProviderSelected = (
	adminToolsProvider: IGlobalPositioningService | IKeyManagementSystem,
	selectedProvider: Partial<TSelectedProvider> | null,
	updatedProvider: UpdateProvider | null
) => {
	return adminToolsProvider && (selectedProvider?.providerName === updatedProvider?.providerName || !selectedProvider?.providerName);
};

const areJustOneRole = (roles: TProviderRole[], otherProviderRoles: AuthRoleStatus[]) => {
	return roles?.length === 1 && otherProviderRoles?.length === 1;
};

const areProvidersEquivalentAndValid = (selectedProvider: Partial<TSelectedProvider> | null, theProvider: TSelectedProvider) => {
	return selectedProvider?.providerName === theProvider?.providerName;
};

const getSelectedProvider = <T extends object = TSelectedProvider>({
	providers,
	selectedProvider,
	adminToolsProvider,
}: TGetSelectedProvider): T | null => {
	if (!providers) {
		return null;
	}

	const theProvider = getProviderValues(adminToolsProvider, selectedProvider) as TSelectedProvider;

	let element = null;

	if (isDBProviderSelected(adminToolsProvider, selectedProvider, theProvider)) {
		const matchedProvider = providers?.find((provider) => provider.providerName === theProvider?.providerName);

		if (!matchedProvider) {
			return null;
		}

		const { imageUrl, auth } = matchedProvider ?? {};
		const { roles } = auth ?? {};

		let authType: AuthenticationType | undefined;
		if (roles?.length === 1) {
			authType = roles[0].authType;
		}

		return {
			...adminToolsProvider,
			...theProvider,
			auth,
			imageUrl,
			role: areJustOneRole(roles, theProvider?.authRoleStatuses ?? [])
				? theProvider?.authRoleStatuses?.[0].role
				: selectedProvider?.role,
			authType: selectedProvider?.authType ?? authType,
			authRoleStatuses: roles.map((role) => {
				const selectedRole = theProvider?.authRoleStatuses?.find((statusRole) => statusRole.role === role.role);
				return {
					...role,
					...selectedRole,
				};
			}),
			provider: matchedProvider.provider,
			providerName: matchedProvider.providerName,
			providerDBProperty: matchedProvider.providerDBProperty,
		} as T;
	}

	if (!selectedProvider) {
		return null;
	}

	if (areProvidersEquivalentAndValid(selectedProvider, theProvider)) {
		element = {
			...selectedProvider,
			role: selectedProvider?.authRoleStatuses?.find((role) => role.isLoggedIn)?.role,
		} as T;
		return element;
	}

	if (!areProvidersEquivalentAndValid(selectedProvider, theProvider)) {
		const defaultProvider = getDefaultProvider<Partial<TSelectedProvider>>(selectedProvider, theProvider);
		element = {
			...defaultProvider,
		} as T;
		return element;
	}

	return null;
};

const getDefaultProvider = <T>(selectedProvider: Partial<TSelectedProvider> | null, updatedProvider: TSelectedProvider) => {
	const providerRoles = selectedProvider?.auth?.roles;
	const isSingleRole = providerRoles?.length === 1;

	return {
		...selectedProvider,
		role: isSingleRole ? providerRoles[0].role : selectedProvider?.role,
		authRoleStatuses: selectedProvider?.auth?.roles,
		authType: isSingleRole ? providerRoles[0].authType : selectedProvider?.authType,
		providerName: updatedProvider?.providerName ?? selectedProvider?.providerName,
		providerDBProperty: updatedProvider?.providerDBProperty ?? selectedProvider?.providerDBProperty,
	} as T;
};

const getProviderValues = (
	adminToolsProvider: IGlobalPositioningService | IKeyManagementSystem,
	selectedProvider: Partial<TSelectedProvider> | null
): {
	providerName: string;
	providerDBProperty: string;
	authRoleStatuses?: AuthRoleStatus[];
	authType?: AuthenticationType;
	auth: {
		roles: {
			role: string;
			authType: AuthenticationType;
		}[];
	};
} | null => {
	if (!adminToolsProvider) {
		return null;
	}

	if (!selectedProvider) {
		// Loop through the adminToolsProvider object and return the first object whose value aren't null
		const provKeys = Object.keys(adminToolsProvider) as (keyof typeof adminToolsProvider)[];
		const foundedKey = provKeys.find((key) => Boolean(adminToolsProvider[key])) as keyof typeof adminToolsProvider;
		return adminToolsProvider[foundedKey];
	}

	const keys = Object.keys(adminToolsProvider) as (keyof typeof adminToolsProvider)[];

	const matchedKey = keys.find((key) => key === selectedProvider?.providerDBProperty);

	return adminToolsProvider[matchedKey as keyof typeof adminToolsProvider];
};

const hasSomeRoleLoggedIn = (selectedProvider: Partial<TSelectedProvider> | null) => {
	if (!selectedProvider?.authRoleStatuses?.length) {
		return false;
	}
	return selectedProvider?.authRoleStatuses?.some((role) => role.isLoggedIn);
};

const hasMultipleRoles = (selectedProvider: Partial<TSelectedProvider> | null) => {
	if (!selectedProvider?.authRoleStatuses?.length) {
		return false;
	}

	return selectedProvider?.authRoleStatuses?.length > 1;
};

export { hasMultipleRoles, hasSomeRoleLoggedIn, getSelectedProvider, getProviderValues };
